import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Point } from 'highcharts';
import { filter, get, isNumber } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from 'src/components/common/Breadcrumbs';
import ButtonBack from 'src/components/common/ButtonBack';
import MainContainer from 'src/components/common/MainContainer';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import TableHorizontal from 'src/components/TableHorizontal';
import { VesselDetailBox } from 'src/features/VesselDetail/config';
import VesselDetailLoading from 'src/features/VesselDetail/VesselDetailLoading';
import VesselMetrics from 'src/features/VesselDetail/VesselMetrics';
import { useQuerySummaryVesselDetail } from 'src/services/queries/useQueryVessel';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { convertToYoutubeEmbedLink } from 'src/utils/helpers/utilities';

const VesselDetail = () => {
  const { id } = useParams();
  const {
    data: { data, positions, schedules },
    isFetching,
  } = useQuerySummaryVesselDetail({ mmsi: id ?? '' });

  const dataPoints = useMemo(() => filter(positions, ({ lat, lon }) => isNumber(lat) && isNumber(lon)), [positions]);

  const dataLines = useMemo(() => {
    return dataPoints.slice(0, -1).map((position, i) => {
      return {
        geometry: {
          type: 'LineString',
          coordinates: [
            [position.lon, position.lat],
            [dataPoints[i + 1].lon, dataPoints[i + 1].lat],
          ],
        },
      };
    });
  }, [dataPoints]);

  const title = (
    <Box>
      <Typography variant="h5">{renderTextOrDefault(data.name)}</Typography>
      <Typography display={'inline'} variant="body2" color={'text.secondary'}>
        IMO:{' '}
        <Typography display={'inline'} variant="subtitle1" color={'primary.main'}>
          {data.imo}
        </Typography>
      </Typography>
      <Typography ml={5} display={'inline'} variant="body2" color={'text.secondary'}>
        MMSI:{' '}
        <Typography display={'inline'} variant="subtitle1" color={'primary.main'}>
          {data.mmsi}
        </Typography>
      </Typography>
    </Box>
  );

  const table = (
    <VesselDetailBox flexBasis={'50%'} p={2}>
      <Typography mb={2} variant="h6">
        {data.ship_type}
      </Typography>
      <TableHorizontal
        colKeyWidth={180}
        configColumn={[
          {
            label: 'Callsign',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.callsign)}
                </Typography>
              );
            },
          },
          {
            label: 'DWT',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.dwt)}
                </Typography>
              );
            },
          },
          {
            label: 'Builder',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.builder)}
                </Typography>
              );
            },
          },
          {
            label: 'Owner',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.owner)}
                </Typography>
              );
            },
          },
          {
            label: 'TEU',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.teu)}
                </Typography>
              );
            },
          },
          {
            label: 'Length',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.length)}
                </Typography>
              );
            },
          },
          {
            label: 'Width',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.width)}
                </Typography>
              );
            },
          },
          {
            label: 'Year Build',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.year_build)}
                </Typography>
              );
            },
          },
          // {
          //   label: 'Draught',
          //   renderCell(data: TypeVesselDetail) {
          //     return (
          //       <Typography variant="body2" color={'text.secondary'}>
          //         {EmptyText}
          //       </Typography>
          //     );
          //   },
          // },
          {
            label: 'Operator',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.operator)}
                </Typography>
              );
            },
          },
          {
            label: 'Active Service',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.service)}
                </Typography>
              );
            },
          },
          {
            label: 'Country',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.country)}
                </Typography>
              );
            },
          },
          {
            label: 'Owner',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {renderTextOrDefault(data.owner)}
                </Typography>
              );
            },
          },
          {
            label: 'Last seen',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {dayjs(data.last_seen).format(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A)}
                </Typography>
              );
            },
          },
          {
            label: 'Status',
            renderCell(data: TypeVesselDetail) {
              return (
                <Typography variant="body2" color={'text.secondary'}>
                  {data.status}
                </Typography>
              );
            },
          },
        ]}
        data={data}
      />
    </VesselDetailBox>
  );

  const worldMap = useMemo(
    () => (
      <WorldMapChart
        dataLine={dataLines}
        tooltipFormatter={function () {
          const point: Point = this.point;
          const getValueFromPoint = (key: string) => get(point.options, key) ?? EmptyText;
          return `<div style="display: flex; flex-direction: column">
                  <span style="font-weight: 600; font-size: 16px">${getValueFromPoint('ref_period')}</span>
                  <span>Destination: ${getValueFromPoint('destination')}</span>
                  <span>ETA: ${dayjs(getValueFromPoint('eta')).format(FORMAT_DATE_TIME.DD_MM_YYYY_HH_MM_A)}</span>
                  <span>Status: ${getValueFromPoint('status')}</span>
              </div>`;
        }}
        height={350}
        dataPoint={dataPoints}
        data={[]}
        type={WorldMapOptions.vessel_detail}
      />
    ),
    [dataPoints],
  );

  const schedule = (
    <VesselDetailBox p={2}>
      <Stack justifyContent={'center'} mt={2} spacing={2}>
        {/* <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography variant="h6">Schedule</Typography>
          <Stack direction={'row'} gap={1}>
            {ListButtonFilterSchedule.map((item, index) => {
              const isActive = filterSchedule === item.value;
              return (
                <ButtonFilterSchedule
                  boxProps={{
                    onClick: () => setFilterSchedule(item.value),
                  }}
                  key={index}
                  isActive={isActive}
                >
                  {item.label}
                </ButtonFilterSchedule>
              );
            })}
          </Stack>
        </Stack> */}

        {/* <CustomTable
          isPagination={false}
          isHover={false}
          dataKey={'name'}
          data={schedules}
          configColumn={
            [
              {
                label: 'Name',
                width: 150,
                cell(data: TypeVesselDetailSchedule) {
                  return (
                    <Typography variant="body2" color={'text.secondary'}>
                      {renderTextOrDefault(data.name)}
                    </Typography>
                  );
                },
              },
              {
                label: 'Locode',
                cell(data: TypeVesselDetailSchedule) {
                  return (
                    <RouterLinkNoUnderline to={AuthRouteKeys.PortDetail.replace(':id?', data.locode || '')}>
                      <Typography variant="body2" color={'secondary'}>
                        {renderTextOrDefault(data.locode)}
                      </Typography>
                    </RouterLinkNoUnderline>
                  );
                },
              },
              {
                label: 'Start Date',
                width: 150,
                cell(data: TypeVesselDetailSchedule) {
                  return (
                    <Typography variant="body2" color={'text.secondary'}>
                      {renderTextOrDefault(data.start_date)}
                    </Typography>
                  );
                },
              },
              {
                label: 'End Date',
                width: 150,
                cell(data: TypeVesselDetailSchedule) {
                  return (
                    <Typography variant="body2" color={'text.secondary'}>
                      {renderTextOrDefault(data.end_date)}
                    </Typography>
                  );
                },
              },
              {
                label: 'Services',
                cell(data: TypeVesselDetailSchedule) {
                  return (
                    <Typography variant="body2" color={'text.secondary'}>
                      {renderTextOrDefault(data.services)}
                    </Typography>
                  );
                },
              },
            ] as ConfigCell<TypeVesselDetailSchedule>[]
          }
        /> */}
      </Stack>
    </VesselDetailBox>
  );

  const metrics = <VesselMetrics />;

  if (isFetching) {
    return <VesselDetailLoading />;
  }

  const breadcrumb = (
    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
      <ButtonBack />
      <Breadcrumbs
        listItem={[
          {
            text: 'Trade Data',
          },
          {
            text: renderTextOrDefault(data.country_name),
          },
          {
            text: renderTextOrDefault(data.name),
          },
        ]}
      />
    </Stack>
  );

  return (
    <MainContainer>
      <SectionErrorBoundary resetKey="VesselDetail" sectionName="VesselDetail">
        <Stack spacing={3}>
          {breadcrumb}
          <VesselDetailBox>
            <Stack spacing={2}>
              {title}
              <Stack spacing={2} direction={'row'}>
                {table}
                <Stack flexBasis={'50%'} spacing={2}>
                  {worldMap}
                  {/* {schedule} */}
                </Stack>
              </Stack>
            </Stack>
          </VesselDetailBox>
          <VesselDetailBox>{metrics}</VesselDetailBox>
          {data.youtube_url && (
            <VesselDetailBox>
              <Typography mb={3} variant="h6">
                Video
              </Typography>
              <iframe
                frameBorder={0}
                style={{ borderRadius: 16 }}
                width={'100%'}
                height="700px"
                src={convertToYoutubeEmbedLink(data.youtube_url)}
                title={data.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              ></iframe>
            </VesselDetailBox>
          )}
        </Stack>
      </SectionErrorBoundary>
    </MainContainer>
  );
};

export default VesselDetail;
